// Header Management Module
// Handles the application header, title, and app selection logic

import { CONFIG } from '../config.js';
import { getToken, handleAuthError } from './auth.js';

// Default title
const DEFAULT_TITLE = "Cyber Schooling";

/**
 * Initializes the header module
 */
export function initHeader() {
    console.log('Header Module Initialized');

    // Set initial title (ensure it matches default)
    setAppTitle(DEFAULT_TITLE);

    // Make functions globally available for testing/simulations
    window.setAppTitle = setAppTitle;
    window.loadApp = loadApp;
}

/**
 * Updates the header title
 * @param {string} title - The new title to display
 */
export function setAppTitle(title) {
    const titleElement = document.getElementById('headerTitle');
    if (titleElement) {
        titleElement.textContent = title;
        console.log(`Header title updated to: ${title}`);
    } else {
        console.warn('Header title element not found');
    }
}

/**
 * Simulates loading an app and updating the title.
 * Tries to fetch from backend, falls back to local data.
 * @param {number|string} appId - The ID of the app to load
 */
export async function loadApp(appId) {
    console.log(`Loading app with ID: ${appId}`);

    // Fallback data (since DB might not be ready)
    const mockApps = {
        '1': { name: 'Zinsrechnung', icon: 'calculator' },
        '2': { name: 'Vokabeltrainer', icon: 'book' },
        '3': { name: 'Mathe-Quiz', icon: 'puzzle-piece' }
    };

    let appName = null;

    try {
        // Try to fetch from API (User mentioned "aus der Datenbank")
        // Note: The endpoint /apps currently returns 404, but we implement logic for when it's ready
        const token = getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${CONFIG.APP_API_BASE}/api/apps/${appId}`, {
            headers
        });

        if (!response.ok) {
            // Check for auth errors (401/429)
            const authResult = await handleAuthError(response);
            if (authResult.handled) {
                // Auth error was handled - return early
                return;
            }
            // Other errors - will fall through to use mock data
        }

        if (response.ok) {
            const appData = await response.json();
            // Assuming localized name structure, or just 'name'
            // "der name ist ja in jeder sprache individuell vorhanden"
            // We'll check if name is an object or string
            if (typeof appData.name === 'object') {
                const lang = window.i18n ? window.i18n.getCurrentLanguage() : 'de';
                appName = appData.name[lang] || appData.name['en'] || Object.values(appData.name)[0];
            } else {
                appName = appData.name;
            }
        }
    } catch (error) {
        console.warn('Failed to fetch app from API:', error);
    }

    // If API failed or returned nothing, use mock
    if (!appName && mockApps[appId]) {
        appName = mockApps[appId].name;
    }

    // Update title if we found a name
    if (appName) {
        setAppTitle(appName);

        // Ensure "AppPlatform" branding is still visible?
        // User said: "Dann muss der App.name aus der Datenbank.. links neben dem icon stehen"
        // This replaces "App-Plattform".
    } else {
        console.warn(`App with ID ${appId} not found.`);
        // Optional: Reset to default?
        // setAppTitle(DEFAULT_TITLE);
    }
}
